import { formatCurrency } from './formatters';

// COR-272: cada gauge do cockpit da aba "TODOS" (AuditoriaGauge.tsx) mostra só um número
// agregado ("35 de 627") — o usuário pediu para poder clicar em qualquer um deles e ver, num
// modal, TUDO que compõe aquele número (quais módulos, quais tributos, quais contas), para saber
// exatamente o que procurar no filtro principal da auditoria. Mesmo padrão de separação já usado
// em auditoriaCockpit.ts: lógica pura e testável aqui, renderização em componente React à parte
// (AuditoriaCockpitDetalheModal.tsx).

export interface ColunaDetalheCockpit {
  chave: string;
  rotulo: string;
  alinhamento?: 'left' | 'right' | 'center';
}

export interface DetalheCockpit {
  titulo: string;
  descricao: string;
  colunas: ColunaDetalheCockpit[];
  linhas: Record<string, string>[];
  textoVazio: string;
}

export interface ModuloDetalheCockpit {
  nome: string;
  erros: number;
  alertas: number;
  avisos: number;
  fiscal: number;
  financeiro: number;
}

// COR-304: um tributo pode divergir em mais de um trimestre — antes só o status/mensagem
// consolidado do tributo era exibido, sem indicar QUAIS trimestres especificamente divergiram.
export interface TrimestreDivergenteCockpit {
  periodo: string;
  status: string;
}

export interface TributoCardDetalheCockpit {
  tributo: string;
  status: string;
  mensagem: string;
  trimestresDivergentes: TrimestreDivergenteCockpit[];
}

export interface TrimestreCompensacaoDetalheCockpit {
  tributo: string;
  periodo: string;
  valor: number;
}

export interface ContaInvertidaDetalheCockpit {
  empresaNome: string;
  contaContabil: string;
  descricaoConta: string;
  naturezaEsperada: string;
  naturezaEncontrada: string;
  saldoFinal: number;
}

export interface EmpresaDetalheCockpit {
  nome: string;
  cnpj: string;
  selecionada: boolean;
}

export interface EntradaDetalhesCockpit {
  modulos: ModuloDetalheCockpit[];
  cardsTributos: TributoCardDetalheCockpit[];
  compensacoesPendentesTrimestres: TrimestreCompensacaoDetalheCockpit[];
  contasComNaturezaInvertida: ContaInvertidaDetalheCockpit[];
  empresas: EmpresaDetalheCockpit[];
  modulosPendentesCarregamento: string[];
}

export type ChaveGaugeCockpit =
  | 'progressoAuditoria'
  | 'errosCriticos'
  | 'alertas'
  | 'avisos'
  | 'impactoFiscalTotal'
  | 'impactoFinanceiroTotal'
  | 'tributosComDivergencia'
  | 'compensacoesPendentes'
  | 'saldosInvertidos'
  | 'empresasNaAuditoria'
  | 'modulosConciliados'
  | 'pendenciasDoRevisor'
  | 'modulosComImpacto'
  | 'areasCriticas'
  | 'dependenciasExternas'
  | 'modulosPendentesCarregamento';

// Módulos com dependência real de serviço externo (BCB Selic/PTAX) — mesma lista/contagem
// documentada em DEPENDENCIAS_EXTERNAS_CONHECIDAS (auditoriaCockpit.ts).
export const MODULOS_DEPENDENCIA_EXTERNA = ['Cambial', 'Round Tripping', 'Mutação Volátil'];

const colModulo: ColunaDetalheCockpit = { chave: 'modulo', rotulo: 'Módulo' };

function porOcorrencias(chaveOrdenacao: 'erros' | 'alertas' | 'avisos') {
  return (modulos: ModuloDetalheCockpit[]): DetalheCockpit['linhas'] =>
    modulos
      .filter(m => m[chaveOrdenacao] > 0)
      .sort((a, b) => b[chaveOrdenacao] - a[chaveOrdenacao])
      .map(m => ({ modulo: m.nome, ocorrencias: String(m[chaveOrdenacao]) }));
}

export function montarDetalhesCockpit(entrada: EntradaDetalhesCockpit): Record<ChaveGaugeCockpit, DetalheCockpit> {
  const { modulos, cardsTributos, compensacoesPendentesTrimestres, contasComNaturezaInvertida, empresas, modulosPendentesCarregamento } = entrada;

  const modulosComImpacto = modulos
    .filter(m => m.fiscal > 0 || m.financeiro > 0)
    .sort((a, b) => (b.fiscal + b.financeiro) - (a.fiscal + a.financeiro));

  const modulosComErro = modulos.filter(m => m.erros > 0).sort((a, b) => b.erros - a.erros);
  const modulosComAviso = modulos.filter(m => m.avisos > 0).sort((a, b) => b.avisos - a.avisos);

  return {
    progressoAuditoria: {
      titulo: 'Progresso da Auditoria',
      descricao: modulosPendentesCarregamento.length === 0
        ? 'Todos os módulos foram carregados com sucesso nesta consulta.'
        : `${modulosPendentesCarregamento.length} módulo(s) falharam ao carregar nesta consulta — ver lista abaixo.`,
      colunas: [colModulo, { chave: 'status', rotulo: 'Status' }],
      linhas: modulosPendentesCarregamento.map(nome => ({ modulo: nome, status: 'Falhou ao carregar' })),
      textoVazio: 'Nenhuma falha de carregamento — 100% dos módulos tentados carregaram com sucesso.',
    },

    errosCriticos: {
      titulo: 'Erros Críticos',
      descricao: 'Ocorrências classificadas como ERRO em cada módulo — inconsistências críticas que exigem correção imediata.',
      colunas: [colModulo, { chave: 'ocorrencias', rotulo: 'Ocorrências', alinhamento: 'right' }],
      linhas: porOcorrencias('erros')(modulos),
      textoVazio: 'Nenhum erro crítico identificado nos módulos carregados.',
    },

    alertas: {
      titulo: 'Alertas',
      descricao: 'Ocorrências classificadas como ALERTA em cada módulo — risco tributário ou fiscal identificado.',
      colunas: [colModulo, { chave: 'ocorrencias', rotulo: 'Ocorrências', alinhamento: 'right' }],
      linhas: porOcorrencias('alertas')(modulos),
      textoVazio: 'Nenhum alerta identificado nos módulos carregados.',
    },

    avisos: {
      titulo: 'Avisos',
      descricao: 'Ocorrências classificadas como AVISO em cada módulo — pontos de atenção que requerem validação.',
      colunas: [colModulo, { chave: 'ocorrencias', rotulo: 'Ocorrências', alinhamento: 'right' }],
      linhas: porOcorrencias('avisos')(modulos),
      textoVazio: 'Nenhum aviso identificado nos módulos carregados.',
    },

    impactoFiscalTotal: {
      titulo: 'Impacto Fiscal Total',
      descricao: 'Imposto presumido, multas e penalidades por módulo — soma o Impacto Fiscal Total do cockpit.',
      colunas: [colModulo, { chave: 'valor', rotulo: 'Impacto Fiscal', alinhamento: 'right' }],
      linhas: modulos
        .filter(m => m.fiscal > 0)
        .sort((a, b) => b.fiscal - a.fiscal)
        .map(m => ({ modulo: m.nome, valor: formatCurrency(m.fiscal) })),
      textoVazio: 'Nenhum módulo com impacto fiscal identificado.',
    },

    impactoFinanceiroTotal: {
      titulo: 'Impacto Financeiro Total',
      descricao: 'Valor monetário das irregularidades por módulo — soma o Impacto Financeiro Total do cockpit.',
      colunas: [colModulo, { chave: 'valor', rotulo: 'Impacto Financeiro', alinhamento: 'right' }],
      linhas: modulos
        .filter(m => m.financeiro > 0)
        .sort((a, b) => b.financeiro - a.financeiro)
        .map(m => ({ modulo: m.nome, valor: formatCurrency(m.financeiro) })),
      textoVazio: 'Nenhum módulo com impacto financeiro identificado.',
    },

    tributosComDivergencia: {
      titulo: 'Tributos com Divergência',
      descricao: 'Dos tributos fiscais (IRPJ/CSLL/PIS/COFINS), os que têm alguma divergência (status diferente de CONCILIADO).',
      colunas: [
        { chave: 'tributo', rotulo: 'Tributo' },
        { chave: 'status', rotulo: 'Status' },
        // COR-304: antes só o status/diagnóstico consolidado do tributo era exibido — agora
        // também lista quais trimestres especificamente divergiram, não só o tributo como um todo.
        { chave: 'trimestres', rotulo: 'Trimestres com Divergência' },
        { chave: 'mensagem', rotulo: 'Diagnóstico' },
      ],
      linhas: cardsTributos
        .filter(c => c.status !== 'CONCILIADO')
        .map(c => ({
          tributo: c.tributo,
          status: c.status,
          trimestres: c.trimestresDivergentes.length > 0
            ? c.trimestresDivergentes.map(t => `${t.periodo} (${t.status})`).join(', ')
            : '—',
          mensagem: c.mensagem,
        })),
      textoVazio: 'Nenhum tributo com divergência — todos os 4 tributos (IRPJ/CSLL/PIS/COFINS) estão conciliados.',
    },

    compensacoesPendentes: {
      titulo: 'Compensações Pendentes',
      descricao: 'Trimestres com potencial de compensação identificado e ainda não aproveitado.',
      colunas: [
        { chave: 'tributo', rotulo: 'Tributo' },
        { chave: 'periodo', rotulo: 'Trimestre' },
        { chave: 'valor', rotulo: 'Potencial Não Aproveitado', alinhamento: 'right' },
      ],
      linhas: compensacoesPendentesTrimestres
        .filter(t => t.valor > 0)
        .sort((a, b) => b.valor - a.valor)
        .map(t => ({ tributo: t.tributo, periodo: t.periodo, valor: formatCurrency(t.valor) })),
      textoVazio: 'Nenhum trimestre com potencial de compensação pendente.',
    },

    saldosInvertidos: {
      titulo: 'Saldos Invertidos',
      descricao: 'Contas de Fornecedores/Clientes com a natureza de saldo trocada (Passivo credor esperado, Ativo devedor esperado).',
      colunas: [
        { chave: 'empresa', rotulo: 'Empresa' },
        { chave: 'conta', rotulo: 'Conta' },
        { chave: 'esperada', rotulo: 'Natureza Esperada' },
        { chave: 'encontrada', rotulo: 'Natureza Encontrada' },
        { chave: 'saldo', rotulo: 'Saldo Final', alinhamento: 'right' },
      ],
      linhas: contasComNaturezaInvertida.map(c => ({
        empresa: c.empresaNome,
        conta: `${c.contaContabil} - ${c.descricaoConta}`,
        esperada: c.naturezaEsperada,
        encontrada: c.naturezaEncontrada,
        saldo: formatCurrency(c.saldoFinal),
      })),
      textoVazio: 'Nenhuma conta com natureza de saldo invertida identificada.',
    },

    empresasNaAuditoria: {
      titulo: 'Empresas na Auditoria',
      descricao: 'Empresas cadastradas para o cliente — a(s) marcada(s) "Sim" está(ão) sendo exibida(s) nesta consulta.',
      colunas: [
        { chave: 'empresa', rotulo: 'Empresa' },
        { chave: 'cnpj', rotulo: 'CNPJ' },
        { chave: 'selecionada', rotulo: 'Nesta Consulta', alinhamento: 'center' },
      ],
      linhas: empresas.map(e => ({ empresa: e.nome, cnpj: e.cnpj, selecionada: e.selecionada ? 'Sim' : 'Não' })),
      textoVazio: 'Nenhuma empresa cadastrada para este cliente.',
    },

    modulosConciliados: {
      titulo: 'Módulos Conciliados',
      descricao: 'Módulos sem nenhum impacto fiscal ou financeiro identificado no período — acompanhados apenas para referência.',
      colunas: [colModulo],
      linhas: modulos
        .filter(m => m.fiscal === 0 && m.financeiro === 0)
        .map(m => ({ modulo: m.nome })),
      textoVazio: 'Nenhum módulo conciliado — todos têm algum impacto identificado.',
    },

    pendenciasDoRevisor: {
      titulo: 'Pendências do Revisor',
      descricao: 'Módulos com pelo menos um AVISO — item que requer validação manual do responsável.',
      colunas: [colModulo, { chave: 'avisos', rotulo: 'Avisos', alinhamento: 'right' }],
      linhas: modulosComAviso.map(m => ({ modulo: m.nome, avisos: String(m.avisos) })),
      textoVazio: 'Nenhum módulo com pendência de revisão.',
    },

    modulosComImpacto: {
      titulo: 'Módulos com Impacto',
      descricao: 'Módulos com algum impacto fiscal ou financeiro identificado no período.',
      colunas: [
        colModulo,
        { chave: 'fiscal', rotulo: 'Impacto Fiscal', alinhamento: 'right' },
        { chave: 'financeiro', rotulo: 'Impacto Financeiro', alinhamento: 'right' },
      ],
      linhas: modulosComImpacto.map(m => ({
        modulo: m.nome,
        fiscal: formatCurrency(m.fiscal),
        financeiro: formatCurrency(m.financeiro),
      })),
      textoVazio: 'Nenhum módulo com impacto identificado.',
    },

    areasCriticas: {
      titulo: 'Áreas Críticas',
      descricao: 'Módulos com pelo menos um ERRO — inconsistência crítica que exige correção imediata.',
      colunas: [colModulo, { chave: 'erros', rotulo: 'Erros', alinhamento: 'right' }],
      linhas: modulosComErro.map(m => ({ modulo: m.nome, erros: String(m.erros) })),
      textoVazio: 'Nenhum módulo com erro crítico.',
    },

    dependenciasExternas: {
      titulo: 'Dependências Externas',
      descricao: 'Módulos que dependem de serviço externo (BCB Selic/PTAX) para o cálculo — indisponibilidade da fonte pode afetar o resultado.',
      colunas: [colModulo, { chave: 'motivo', rotulo: 'Motivo' }],
      linhas: MODULOS_DEPENDENCIA_EXTERNA.map(nome => ({ modulo: nome, motivo: 'Depende de taxa BCB (Selic/PTAX)' })),
      textoVazio: 'Nenhuma dependência externa conhecida.',
    },

    modulosPendentesCarregamento: {
      titulo: 'Módulos Pendentes',
      descricao: 'Módulos que falharam ao carregar nesta consulta (ver console do navegador para o erro técnico).',
      colunas: [colModulo],
      linhas: modulosPendentesCarregamento.map(nome => ({ modulo: nome })),
      textoVazio: 'Nenhum módulo pendente — todos carregaram com sucesso nesta consulta.',
    },
  };
}
